import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <ThemeToggle />
      <div className="hidden md:flex flex-col justify-between relative bg-card border-r border-border p-12 overflow-hidden">
        <div className="absolute inset-0 hero-gradient pointer-events-none" />
        <div className="absolute inset-0 sovereign-grid opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-display text-base font-medium">Sovereign VDR</span>
          </div>
        </div>
        <div className="relative">
          <div className="label-mono text-muted-foreground">— Account recovery</div>
          <h2 className="mt-4 font-display text-4xl tracking-tighter font-medium leading-tight">
            One link.<br />One hour.<br />Back in the room.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/login" className="label-mono text-muted-foreground inline-flex items-center gap-2 hover:text-foreground">
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
          <h1 className="mt-6 font-display text-3xl font-medium tracking-tight">Reset password</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter your account email and we&rsquo;ll send a secure reset link. The link expires in one hour.
          </p>

          {!done ? (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="label-mono">Email</label>
                <input
                  data-testid={AUTH.forgotEmail}
                  type="email" required autoFocus
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full px-3 py-2.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              {err && <div className="text-sm text-destructive">{err}</div>}
              <button
                data-testid={AUTH.forgotSubmit}
                type="submit" disabled={loading}
                className="w-full px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
          ) : (
            <div className="mt-8 border border-border bg-secondary/40 rounded-md p-5">
              <div className="label-mono text-muted-foreground">— Check your inbox</div>
              <p className="mt-3 text-sm">
                If <span className="font-medium">{email}</span> is registered, a password-reset link is on its way.
                It expires in one hour.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Didn't get it? Check spam, or wait a minute and try again.
              </p>
              <Link
                to="/login"
                className="mt-5 inline-flex w-full justify-center px-4 py-2.5 rounded-md border border-border hover:bg-secondary text-sm transition"
              >
                Return to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
